import React, { useState, useEffect } from "react";
import { Table, Typography, message, Tag, Modal, Spin, Divider, Row, Col } from "antd";
import CouponServ from "../../../service/CouponServ";
import { formatDate } from "../../../util/dateUtils";
import DataTable from "../../../component/ui/DataTable";
import { usePreference } from "../../../context/PreferenceContext";
import { FilterBar } from "../../../component/ui/FilterBar";
import SearchInput from "../../../component/ui/SearchInput";
import { ReloadOutlined } from "@ant-design/icons";
import { ResetButton } from "../../../component/ui/ActionButtons";
import OrdersServ from "../../../service/OrdersServ";

const { Text } = Typography;

const CouponUsage = () => {
    const { formatPrice } = usePreference();
    const [loading, setLoading] = useState(false);
    const [usageLogs, setUsageLogs] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalLogs, setTotalLogs] = useState(0);
    const [searchText, setSearchText] = useState("");

    const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
    const [orderLoading, setOrderLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const handleViewOrder = async (orderObjectId) => {
        if (!orderObjectId) return;
        try {
            setOrderLoading(true);
            setIsOrderModalOpen(true);
            const response = await OrdersServ.single(orderObjectId);
            if (response) {
                setSelectedOrder(response.data || response);
            }
        } catch (error) {
            message.error("Failed to load order details.");
            setIsOrderModalOpen(false);
        } finally {
            setOrderLoading(false);
        }
    };

    const fetchUsage = async (p = page) => {
        try {
            setLoading(true);
            const params = {
                limit: pageSize,
                skip: (p - 1) * pageSize,
                sort: JSON.stringify({ createdAt: -1 }),
                search: searchText
            };
            // Note: Fallback to empty array if backend endpoint isn't fully returning data yet
            const response = await CouponServ.getUsageHistory(params).catch(() => ({ data: [], total: 0 }));
            setUsageLogs(Array.isArray(response?.data) ? response?.data : []);
            setTotalLogs(response?.total || 0);
        } catch (error) {
            message.error("Failed to load usage history.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsage();
    }, [page, pageSize, searchText]);

    const columns = [
        {
            title: "Coupon Code",
            dataIndex: ["coupon", "code"],
            key: "code",
            render: (code) => <Text strong style={{ color: '#4776E6' }}>{code || 'N/A'}</Text>,
        },
        {
            title: "User",
            dataIndex: ["user", "name"],
            key: "user",
            render: (name, record) => (
                <div>
                    <div><Text strong>{name || 'Unknown User'}</Text></div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{record.user?.email || ''}</div>
                </div>
            )
        },
        {
            title: "Order ID",
            dataIndex: ["order", "orderId"],
            key: "orderId",
            render: (orderId, record) => (
                <a 
                    onClick={() => handleViewOrder(record.order?._id)} 
                    style={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                >
                    {orderId || 'N/A'}
                </a>
            )
        },
        {
            title: "Discount Applied",
            dataIndex: "discountApplied",
            key: "discountApplied",
            render: (amount) => <Text type="success" strong>{formatPrice(amount || 0)}</Text>
        },
        {
            title: "Delivery Waived",
            dataIndex: "deliveryDiscountApplied",
            key: "deliveryDiscountApplied",
            render: (amount) => amount > 0 ? <Tag color="cyan">{formatPrice(amount)}</Tag> : '-'
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={status === 'success' ? 'green' : status === 'failed' ? 'red' : 'default'}>
                    {status?.toUpperCase() || 'UNKNOWN'}
                </Tag>
            )
        },
        {
            title: "Used At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => formatDate(date)
        }
    ];

    return (
        <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <FilterBar>
                    <SearchInput
                        label="Search Usage"
                        placeholder="Search by code or email..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </FilterBar>
                <ResetButton 
                    icon={<ReloadOutlined />}
                    onClick={() => {
                        setSearchText("");
                        fetchUsage();
                    }} 
                />
            </div>

            <DataTable
                data={usageLogs}
                columns={columns}
                rowKey="_id"
                loading={loading}
                pagination={{
                    current: page,
                    pageSize: pageSize,
                    total: totalLogs,
                    onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                    showSizeChanger: true,
                }}
            />

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '18px', fontWeight: '800' }}>Order Details</span>
                        {selectedOrder && (
                            <Tag color="blue" style={{ margin: 0 }}>
                                {selectedOrder.orderId}
                            </Tag>
                        )}
                    </div>
                }
                open={isOrderModalOpen}
                onCancel={() => {
                    setIsOrderModalOpen(false);
                    setSelectedOrder(null);
                }}
                footer={null}
                width={600}
                bodyStyle={{ padding: '24px' }}
                className="custom-modal"
            >
                {orderLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin tip="Loading order details..." />
                    </div>
                ) : selectedOrder ? (
                    <div>
                        <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '24px' }}>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>Status</Text>
                                    <div style={{ fontWeight: 600, textTransform: 'uppercase' }}>
                                        <Tag color={selectedOrder.status === 'delivered' ? 'green' : 'orange'}>
                                            {selectedOrder.status}
                                        </Tag>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>Order Date</Text>
                                    <div style={{ fontWeight: 600 }}>{formatDate(selectedOrder.createdAt)}</div>
                                </Col>
                            </Row>
                        </div>

                        <Text strong style={{ fontSize: '15px' }}>Items</Text>
                        <div style={{ marginTop: '12px' }}>
                            {selectedOrder.items?.map((item, index) => (
                                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px dashed #e2e8f0' }}>
                                    <div>
                                        <Text strong>{item.name}</Text>
                                        <div style={{ fontSize: '13px', color: '#64748b' }}>Qty: {item.quantity}</div>
                                    </div>
                                    <Text strong>{formatPrice(item.price * item.quantity)}</Text>
                                </div>
                            ))}
                        </div>

                        <div style={{ marginTop: '24px', background: '#f8fafc', padding: '16px', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <Text type="secondary">Subtotal</Text>
                                <Text strong>{formatPrice(selectedOrder.totalAmount)}</Text>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <Text type="secondary">Delivery Charge</Text>
                                <Text strong>{formatPrice(selectedOrder.deliveryCharge)}</Text>
                            </div>
                            {selectedOrder.couponDiscount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#10b981' }}>
                                    <Text style={{ color: 'inherit' }}>Coupon Discount</Text>
                                    <Text strong style={{ color: 'inherit' }}>-{formatPrice(selectedOrder.couponDiscount)}</Text>
                                </div>
                            )}
                            <Divider style={{ margin: '12px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text strong style={{ fontSize: '16px' }}>Total Payable</Text>
                                <Text strong style={{ fontSize: '18px', color: '#0f172a' }}>{formatPrice(selectedOrder.payableAmount)}</Text>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Text type="secondary">Order details not available.</Text>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default CouponUsage;
